import { useState } from "react";
import { Clock, Plus, Calendar, Trash2, Settings, X } from "lucide-react";
import { Switch } from "../../ui/switch";
import { Dialog, DialogContent } from "../../ui/dialog";
import { format, parseISO } from "date-fns";
import { nl } from "date-fns/locale";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTheme } from "../../../context/ThemeContext";

registerLocale("nl", nl);

const ReservationSettingsModal = ({
  open,
  onOpenChange,
  settings,
  onSave,
  initialDate,
}) => {
  const [localSettings, setLocalSettings] = useState(() => {
    const newSettings = JSON.parse(JSON.stringify(settings));
    newSettings.maxAdvanceDays = newSettings.maxAdvanceDays || 60;
    return newSettings;
  });

  const { theme } = useTheme();
  const isDark = theme === "dark";

  const [activeTab, setActiveTab] = useState(
    initialDate ? "exceptions" : "week",
  );
  const [exceptionDate, setExceptionDate] = useState(initialDate || "");
  const [newSlots, setNewSlots] = useState({});

  const days = [
    { id: 1, label: "Maandag" },
    { id: 2, label: "Dinsdag" },
    { id: 3, label: "Woensdag" },
    { id: 4, label: "Donderdag" },
    { id: 5, label: "Vrijdag" },
    { id: 6, label: "Zaterdag" },
    { id: 0, label: "Zondag" },
  ];

  const generateTimeOptions = () => {
    const options = [];
    for (let i = 0; i < 24; i++) {
      for (let j = 0; j < 60; j += 15) {
        const hour = i.toString().padStart(2, "0");
        const minute = j.toString().padStart(2, "0");
        options.push(`${hour}:${minute}`);
      }
    }
    return options;
  };

  const timeOptions = generateTimeOptions();

  const handleDayChange = (dayId, field, value) => {
    setLocalSettings((prev) => ({
      ...prev,
      standard: {
        ...prev.standard,
        [dayId]: {
          ...prev.standard[dayId],
          [field]: value,
        },
      },
    }));
  };

  const addSlot = (dayId) => {
    const newSlot = newSlots[dayId] || { start: "09:00", end: "10:00" };

    // Validation: end time must be after start time
    const toMinutes = (time) => {
      const [h, m] = time.split(":").map(Number);
      return h * 60 + m;
    };

    if (toMinutes(newSlot.end) <= toMinutes(newSlot.start)) {
      alert("⚠️ Eindtijd moet na de starttijd liggen!");
      return;
    }

    const dayConfig = localSettings.standard[dayId] || {};
    const currentSlots = dayConfig.slots || [];

    // Check overlap with existing slots
    const hasOverlap = currentSlots.some((slot) => {
      const newStart = toMinutes(newSlot.start);
      const newEnd = toMinutes(newSlot.end);
      const existingStart = toMinutes(slot.start);
      const existingEnd = toMinutes(slot.end);
      return newStart < existingEnd && newEnd > existingStart;
    });

    if (hasOverlap) {
      alert("⚠️ Dit tijdslot overlapt met een bestaand slot!");
      return;
    }

    // Check if slot is within general hours
    const generalStart = dayConfig.generalStart || "09:00";
    const generalEnd = dayConfig.generalEnd || "17:00";
    const slotStart = toMinutes(newSlot.start);
    const slotEnd = toMinutes(newSlot.end);
    const genStart = toMinutes(generalStart);
    const genEnd = toMinutes(generalEnd);

    if (slotStart < genStart || slotEnd > genEnd) {
      alert(
        `⚠️ Tijdslot moet binnen de algemene openingstijden liggen (${generalStart} - ${generalEnd})!`,
      );
      return;
    }

    setLocalSettings((prev) => {
      const dayConfig = prev.standard[dayId] || {};
      const currentSlots = dayConfig.slots || [];

      return {
        ...prev,
        standard: {
          ...prev.standard,
          [dayId]: {
            ...dayConfig,
            slots: [...currentSlots, newSlot],
          },
        },
      };
    });

    setNewSlots((prev) => ({
      ...prev,
      [dayId]: { start: "09:00", end: "10:00" },
    }));
  };

  const removeSlot = (dayId, index) => {
    setLocalSettings((prev) => {
      const dayConfig = prev.standard[dayId];
      const newSlots = dayConfig.slots.filter((_, i) => i !== index);
      return {
        ...prev,
        standard: {
          ...prev.standard,
          [dayId]: { ...dayConfig, slots: newSlots },
        },
      };
    });
  };

  const updateNewSlot = (dayId, field, value) => {
    setNewSlots((prev) => ({
      ...prev,
      [dayId]: {
        ...(prev[dayId] || { start: "09:00", end: "10:00" }),
        [field]: value,
      },
    }));
  };

  const handleAddException = () => {
    if (!exceptionDate) return;

    setLocalSettings((prev) => ({
      ...prev,
      exceptions: {
        ...prev.exceptions,
        [exceptionDate]: {
          enabled: true,
          generalStart: "09:00",
          generalEnd: "17:00",
          slots: [],
        },
      },
    }));
    setExceptionDate("");
  };

  const handleRemoveException = (date) => {
    const newExceptions = { ...localSettings.exceptions };
    delete newExceptions[date];
    setLocalSettings((prev) => ({
      ...prev,
      exceptions: newExceptions,
    }));
  };

  const handleExceptionChange = (date, field, value) => {
    setLocalSettings((prev) => ({
      ...prev,
      exceptions: {
        ...prev.exceptions,
        [date]: {
          ...prev.exceptions[date],
          [field]: value,
        },
      },
    }));
  };

  const addExceptionSlot = (date) => {
    const newSlot = newSlots[date] || { start: "09:00", end: "10:00" };

    // Validation: end time must be after start time
    const toMinutes = (time) => {
      const [h, m] = time.split(":").map(Number);
      return h * 60 + m;
    };

    if (toMinutes(newSlot.end) <= toMinutes(newSlot.start)) {
      alert("⚠️ Eindtijd moet na de starttijd liggen!");
      return;
    }

    const excConfig = localSettings.exceptions[date];
    const currentSlots = excConfig.slots || [];

    // Check overlap with existing slots
    const hasOverlap = currentSlots.some((slot) => {
      const newStart = toMinutes(newSlot.start);
      const newEnd = toMinutes(newSlot.end);
      const existingStart = toMinutes(slot.start);
      const existingEnd = toMinutes(slot.end);
      return newStart < existingEnd && newEnd > existingStart;
    });

    if (hasOverlap) {
      alert("⚠️ Dit tijdslot overlapt met een bestaand slot!");
      return;
    }

    // Check if slot is within general hours
    const generalStart = excConfig.generalStart || "09:00";
    const generalEnd = excConfig.generalEnd || "17:00";
    const slotStart = toMinutes(newSlot.start);
    const slotEnd = toMinutes(newSlot.end);
    const genStart = toMinutes(generalStart);
    const genEnd = toMinutes(generalEnd);

    if (slotStart < genStart || slotEnd > genEnd) {
      alert(
        `⚠️ Tijdslot moet binnen de algemene openingstijden liggen (${generalStart} - ${generalEnd})!`,
      );
      return;
    }

    setLocalSettings((prev) => {
      const excConfig = prev.exceptions[date];
      const currentSlots = excConfig.slots || [];

      return {
        ...prev,
        exceptions: {
          ...prev.exceptions,
          [date]: {
            ...excConfig,
            slots: [...currentSlots, newSlot],
          },
        },
      };
    });

    setNewSlots((prev) => ({
      ...prev,
      [date]: { start: "09:00", end: "10:00" },
    }));
  };

  const removeExceptionSlot = (date, index) => {
    setLocalSettings((prev) => {
      const excConfig = prev.exceptions[date];
      const newSlots = excConfig.slots.filter((_, i) => i !== index);
      return {
        ...prev,
        exceptions: {
          ...prev.exceptions,
          [date]: { ...excConfig, slots: newSlots },
        },
      };
    });
  };

  const updateNewExceptionSlot = (date, field, value) => {
    setNewSlots((prev) => ({
      ...prev,
      [date]: {
        ...(prev[date] || { start: "09:00", end: "10:00" }),
        [field]: value,
      },
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`max-w-3xl max-h-[85vh] overflow-y-auto p-0 gap-0 rounded-xl border ${isDark ? "bg-[#141414] border-white/[0.06]" : "bg-white border-gray-200/80"}`}
      >
        {/* Header */}
        <div
          className={`px-6 py-4 border-b ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
        >
          <h2
            className={`text-[14px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
          >
            Reservering Instellingen
          </h2>
          <p
            className={`text-[12px] mt-0.5 ${isDark ? "text-gray-500" : "text-gray-400"}`}
          >
            Beheer uw wekelijkse openingstijden en specifieke tijdsloten.
          </p>
        </div>

        <div className="p-6 space-y-5">
          {/* Global Setting */}
          <div
            className={`rounded-xl p-3 border ${isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-gray-50 border-gray-100"}`}
          >
            <div className="flex items-center gap-3">
              <label
                className={`text-[12px] font-medium whitespace-nowrap ${isDark ? "text-gray-400" : "text-gray-600"}`}
              >
                Max. reserveringstermijn:
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  value={localSettings.maxAdvanceDays}
                  onChange={(e) =>
                    setLocalSettings((prev) => ({
                      ...prev,
                      maxAdvanceDays: parseInt(e.target.value) || 0,
                    }))
                  }
                  className={`w-20 h-8 px-2 rounded-lg text-[13px] border ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white" : "bg-white border-gray-200 text-gray-900"}`}
                />
                <span
                  className={`text-[12px] ${isDark ? "text-gray-500" : "text-gray-400"}`}
                >
                  dagen
                </span>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 p-1 rounded-lg ${isDark ? 'bg-white/[0.04]' : 'bg-gray-100'}">
            {[
              { key: "week", label: "Weekschema" },
              { key: "exceptions", label: "Uitzonderingen" },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex-1 h-8 rounded-md text-[12px] font-medium transition-colors ${
                  activeTab === tab.key
                    ? isDark
                      ? "bg-white text-black"
                      : "bg-gray-900 text-white"
                    : isDark
                      ? "text-gray-400 hover:text-white"
                      : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Week Tab */}
          {activeTab === "week" && (
            <div className="space-y-3">
              {days.map((day) => {
                const config = localSettings.standard[day.id] || {
                  enabled: false,
                  generalStart: "09:00",
                  generalEnd: "17:00",
                  slots: [],
                };

                return (
                  <div
                    key={day.id}
                    className={`rounded-xl border overflow-hidden transition-all ${
                      config.enabled
                        ? isDark
                          ? "bg-white/[0.03] border-white/[0.06]"
                          : "bg-white border-gray-200/80"
                        : isDark
                          ? "bg-white/[0.02] border-white/[0.04] opacity-60"
                          : "bg-gray-50/50 border-gray-100 opacity-60"
                    } hover:opacity-100`}
                  >
                    <div className="p-3 flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <div
                          className={`w-1.5 h-1.5 rounded-full ${config.enabled ? "bg-green-400" : isDark ? "bg-gray-600" : "bg-gray-300"}`}
                        />
                        <span
                          className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                        >
                          {day.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-2.5">
                        <span
                          className={`text-[11px] font-medium ${config.enabled ? (isDark ? "text-green-400" : "text-green-600") : isDark ? "text-gray-600" : "text-gray-400"}`}
                        >
                          {config.enabled ? "Geopend" : "Gesloten"}
                        </span>
                        <Switch
                          checked={config.enabled}
                          onCheckedChange={(checked) =>
                            handleDayChange(day.id, "enabled", checked)
                          }
                        />
                      </div>
                    </div>

                    {config.enabled && (
                      <div
                        className={`px-3 pb-3 pt-2 border-t ${isDark ? "border-white/[0.04]" : "border-gray-100"}`}
                      >
                        <div className="space-y-4">
                          {/* General Hours */}
                          <div className="space-y-1.5">
                            <label
                              className={`text-[11px] font-semibold uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-400"}`}
                            >
                              Openingstijden
                            </label>
                            <div className="flex items-center gap-2">
                              <select
                                value={config.generalStart || "09:00"}
                                onChange={(e) =>
                                  handleDayChange(
                                    day.id,
                                    "generalStart",
                                    e.target.value,
                                  )
                                }
                                className={`flex-1 h-8 px-2 rounded-lg text-[12px] border ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white" : "bg-gray-50 border-gray-200 text-gray-900"}`}
                              >
                                {timeOptions.map((time) => (
                                  <option key={time} value={time}>
                                    {time}
                                  </option>
                                ))}
                              </select>
                              <span
                                className={`text-[12px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
                              >
                                –
                              </span>
                              <select
                                value={config.generalEnd || "17:00"}
                                onChange={(e) =>
                                  handleDayChange(
                                    day.id,
                                    "generalEnd",
                                    e.target.value,
                                  )
                                }
                                className={`flex-1 h-8 px-2 rounded-lg text-[12px] border ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white" : "bg-gray-50 border-gray-200 text-gray-900"}`}
                              >
                                {timeOptions.map((time) => {
                                  const startTime =
                                    config.generalStart || "09:00";
                                  const toMinutes = (t) => {
                                    const [h, m] = t.split(":").map(Number);
                                    return h * 60 + m;
                                  };
                                  return (
                                    <option
                                      key={time}
                                      value={time}
                                      disabled={
                                        toMinutes(time) <= toMinutes(startTime)
                                      }
                                    >
                                      {time}
                                    </option>
                                  );
                                })}
                              </select>
                            </div>
                          </div>

                          {/* Slots */}
                          <div className="space-y-1.5">
                            <label
                              className={`text-[11px] font-semibold uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-400"}`}
                            >
                              Boekbare Tijdsloten
                            </label>
                            <div className="flex flex-wrap gap-1.5 mb-2">
                              {(config.slots || []).map((slot, index) => (
                                <span
                                  key={index}
                                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[12px] font-medium border ${isDark ? "bg-white/[0.04] border-white/[0.06] text-gray-300" : "bg-white border-gray-200 text-gray-700"}`}
                                >
                                  {slot.start} – {slot.end}
                                  <button
                                    onClick={() => removeSlot(day.id, index)}
                                    className={`${isDark ? "text-gray-600 hover:text-red-400" : "text-gray-400 hover:text-red-500"} transition-colors`}
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </span>
                              ))}
                              {(!config.slots || config.slots.length === 0) && (
                                <span
                                  className={`text-[12px] italic ${isDark ? "text-gray-600" : "text-gray-400"}`}
                                >
                                  Geen tijdsloten — alle uren werken op basis
                                  van openingstijden
                                </span>
                              )}
                            </div>
                            <div
                              className={`flex items-center gap-2 p-2 rounded-lg border ${isDark ? "bg-white/[0.02] border-white/[0.04]" : "bg-gray-50 border-gray-100"}`}
                            >
                              <select
                                value={newSlots[day.id]?.start || "09:00"}
                                onChange={(e) =>
                                  updateNewSlot(day.id, "start", e.target.value)
                                }
                                className={`flex-1 h-7 px-2 rounded-md text-[12px] border-0 ${isDark ? "bg-transparent text-white" : "bg-transparent text-gray-900"}`}
                              >
                                {timeOptions.map((time) => (
                                  <option key={time} value={time}>
                                    {time}
                                  </option>
                                ))}
                              </select>
                              <span
                                className={`text-[12px] ${isDark ? "text-gray-600" : "text-gray-300"}`}
                              >
                                –
                              </span>
                              <select
                                value={newSlots[day.id]?.end || "10:00"}
                                onChange={(e) =>
                                  updateNewSlot(day.id, "end", e.target.value)
                                }
                                className={`flex-1 h-7 px-2 rounded-md text-[12px] border-0 ${isDark ? "bg-transparent text-white" : "bg-transparent text-gray-900"}`}
                              >
                                {timeOptions.map((time) => {
                                  const startTime =
                                    newSlots[day.id]?.start || "09:00";
                                  const toMinutes = (t) => {
                                    const [h, m] = t.split(":").map(Number);
                                    return h * 60 + m;
                                  };
                                  return (
                                    <option
                                      key={time}
                                      value={time}
                                      disabled={
                                        toMinutes(time) <= toMinutes(startTime)
                                      }
                                    >
                                      {time}
                                    </option>
                                  );
                                })}
                              </select>
                              <button
                                onClick={() => addSlot(day.id)}
                                className={`h-7 px-3 rounded-md text-[12px] font-medium transition-colors ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
                              >
                                + Toevoegen
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Exceptions Tab */}
          {activeTab === "exceptions" && (
            <div className="space-y-4">
              <div
                className={`rounded-xl p-4 border ${isDark ? "bg-white/[0.03] border-white/[0.06]" : "bg-gray-50 border-gray-100"}`}
              >
                <h3
                  className={`text-[13px] font-semibold mb-3 flex items-center gap-1.5 ${isDark ? "text-white" : "text-gray-900"}`}
                >
                  <Plus
                    className={`w-3.5 h-3.5 ${isDark ? "text-gray-400" : "text-gray-500"}`}
                  />
                  Nieuwe uitzondering
                </h3>
                <div className="flex flex-col sm:flex-row gap-3 items-end">
                  <div className="flex-1 w-full">
                    <label
                      className={`block text-[12px] font-medium mb-1.5 ${isDark ? "text-gray-400" : "text-gray-600"}`}
                    >
                      Datum
                    </label>
                    <DatePicker
                      selected={exceptionDate ? parseISO(exceptionDate) : null}
                      onChange={(date) =>
                        setExceptionDate(date ? format(date, "yyyy-MM-dd") : "")
                      }
                      dateFormat="dd/MM/yyyy"
                      locale="nl"
                      placeholderText="Selecteer een datum"
                      className={`w-full h-9 px-3 rounded-lg text-[13px] border ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white placeholder-gray-600" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400"}`}
                      wrapperClassName="w-full"
                    />
                  </div>
                  <button
                    onClick={handleAddException}
                    disabled={!exceptionDate}
                    className={`h-9 px-4 rounded-lg text-[12px] font-medium transition-colors whitespace-nowrap disabled:opacity-40 ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
                  >
                    Toevoegen
                  </button>
                </div>
              </div>

              {Object.entries(localSettings.exceptions).length === 0 ? (
                <div className="text-center py-12">
                  <div
                    className={`h-12 w-12 rounded-xl mx-auto mb-3 flex items-center justify-center ${isDark ? "bg-white/[0.04]" : "bg-gray-50"}`}
                  >
                    <Calendar
                      className={`h-6 w-6 ${isDark ? "text-gray-600" : "text-gray-300"}`}
                    />
                  </div>
                  <p
                    className={`text-[13px] font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Geen uitzonderingen ingesteld
                  </p>
                  <p
                    className={`text-[12px] mt-1 ${isDark ? "text-gray-600" : "text-gray-400"}`}
                  >
                    Voeg een datum toe om afwijkende tijden in te stellen
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {Object.entries(localSettings.exceptions)
                    .sort((a, b) => new Date(a[0]) - new Date(b[0]))
                    .map(([date, config]) => (
                      <div
                        key={date}
                        className={`rounded-xl border overflow-hidden ${
                          config.enabled
                            ? isDark
                              ? "bg-white/[0.03] border-white/[0.06]"
                              : "bg-white border-gray-200/80"
                            : isDark
                              ? "bg-red-500/5 border-red-500/10"
                              : "bg-red-50/50 border-red-100"
                        }`}
                      >
                        <div className="p-3 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div
                              className={`h-8 w-8 rounded-lg flex items-center justify-center ${config.enabled ? (isDark ? "bg-white/[0.06]" : "bg-gray-100") : isDark ? "bg-red-500/10" : "bg-red-100"}`}
                            >
                              <Calendar
                                className={`w-4 h-4 ${config.enabled ? (isDark ? "text-gray-400" : "text-gray-500") : isDark ? "text-red-400" : "text-red-500"}`}
                              />
                            </div>
                            <div>
                              <span
                                className={`text-[13px] font-semibold ${isDark ? "text-white" : "text-gray-900"}`}
                              >
                                {format(parseISO(date), "dd/MM/yyyy", {
                                  locale: nl,
                                })}
                              </span>
                              <span
                                className={`block text-[11px] ${config.enabled ? (isDark ? "text-gray-500" : "text-gray-400") : isDark ? "text-red-400" : "text-red-500"}`}
                              >
                                {config.enabled
                                  ? "Aangepaste tijden"
                                  : "Gesloten"}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={config.enabled}
                              onCheckedChange={(checked) =>
                                handleExceptionChange(date, "enabled", checked)
                              }
                            />
                            <button
                              onClick={() => handleRemoveException(date)}
                              className={`h-7 w-7 rounded-lg flex items-center justify-center transition-colors ${isDark ? "text-gray-600 hover:text-red-400 hover:bg-red-500/10" : "text-gray-400 hover:text-red-500 hover:bg-red-50"}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>

                        {config.enabled && (
                          <div
                            className={`px-3 pb-3 pt-2 border-t ${isDark ? "border-white/[0.04]" : "border-gray-100"}`}
                          >
                            <div className="space-y-4">
                              {/* General Hours */}
                              <div className="space-y-1.5">
                                <label
                                  className={`text-[11px] font-semibold uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-400"}`}
                                >
                                  Openingstijden
                                </label>
                                <div className="flex items-center gap-2">
                                  <select
                                    value={config.generalStart || "09:00"}
                                    onChange={(e) =>
                                      handleExceptionChange(
                                        date,
                                        "generalStart",
                                        e.target.value,
                                      )
                                    }
                                    className={`flex-1 h-8 px-2 rounded-lg text-[12px] border ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white" : "bg-gray-50 border-gray-200 text-gray-900"}`}
                                  >
                                    {timeOptions.map((time) => (
                                      <option key={time} value={time}>
                                        {time}
                                      </option>
                                    ))}
                                  </select>
                                  <span
                                    className={`text-[12px] ${isDark ? "text-gray-600" : "text-gray-400"}`}
                                  >
                                    –
                                  </span>
                                  <select
                                    value={config.generalEnd || "17:00"}
                                    onChange={(e) =>
                                      handleExceptionChange(
                                        date,
                                        "generalEnd",
                                        e.target.value,
                                      )
                                    }
                                    className={`flex-1 h-8 px-2 rounded-lg text-[12px] border ${isDark ? "bg-white/[0.04] border-white/[0.06] text-white" : "bg-gray-50 border-gray-200 text-gray-900"}`}
                                  >
                                    {timeOptions.map((time) => {
                                      const startTime =
                                        config.generalStart || "09:00";
                                      const toMinutes = (t) => {
                                        const [h, m] = t.split(":").map(Number);
                                        return h * 60 + m;
                                      };
                                      return (
                                        <option
                                          key={time}
                                          value={time}
                                          disabled={
                                            toMinutes(time) <=
                                            toMinutes(startTime)
                                          }
                                        >
                                          {time}
                                        </option>
                                      );
                                    })}
                                  </select>
                                </div>
                              </div>

                              {/* Slots */}
                              <div className="space-y-1.5">
                                <label
                                  className={`text-[11px] font-semibold uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-400"}`}
                                >
                                  Boekbare Tijdsloten
                                </label>
                                <div className="flex flex-wrap gap-1.5 mb-2">
                                  {(config.slots || []).map((slot, index) => (
                                    <span
                                      key={index}
                                      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[12px] font-medium border ${isDark ? "bg-white/[0.04] border-white/[0.06] text-gray-300" : "bg-white border-gray-200 text-gray-700"}`}
                                    >
                                      {slot.start} – {slot.end}
                                      <button
                                        onClick={() =>
                                          removeExceptionSlot(date, index)
                                        }
                                        className={`${isDark ? "text-gray-600 hover:text-red-400" : "text-gray-400 hover:text-red-500"} transition-colors`}
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    </span>
                                  ))}
                                  {(!config.slots ||
                                    config.slots.length === 0) && (
                                    <span
                                      className={`text-[12px] italic ${isDark ? "text-gray-600" : "text-gray-400"}`}
                                    >
                                      Geen specifieke tijdsloten
                                    </span>
                                  )}
                                </div>
                                <div
                                  className={`flex items-center gap-2 p-2 rounded-lg border ${isDark ? "bg-white/[0.02] border-white/[0.04]" : "bg-gray-50 border-gray-100"}`}
                                >
                                  <select
                                    value={newSlots[date]?.start || "09:00"}
                                    onChange={(e) =>
                                      updateNewExceptionSlot(
                                        date,
                                        "start",
                                        e.target.value,
                                      )
                                    }
                                    className={`flex-1 h-7 px-2 rounded-md text-[12px] border-0 ${isDark ? "bg-transparent text-white" : "bg-transparent text-gray-900"}`}
                                  >
                                    {timeOptions.map((time) => (
                                      <option key={time} value={time}>
                                        {time}
                                      </option>
                                    ))}
                                  </select>
                                  <span
                                    className={`text-[12px] ${isDark ? "text-gray-600" : "text-gray-300"}`}
                                  >
                                    –
                                  </span>
                                  <select
                                    value={newSlots[date]?.end || "10:00"}
                                    onChange={(e) =>
                                      updateNewExceptionSlot(
                                        date,
                                        "end",
                                        e.target.value,
                                      )
                                    }
                                    className={`flex-1 h-7 px-2 rounded-md text-[12px] border-0 ${isDark ? "bg-transparent text-white" : "bg-transparent text-gray-900"}`}
                                  >
                                    {timeOptions.map((time) => {
                                      const startTime =
                                        newSlots[date]?.start || "09:00";
                                      const toMinutes = (t) => {
                                        const [h, m] = t.split(":").map(Number);
                                        return h * 60 + m;
                                      };
                                      return (
                                        <option
                                          key={time}
                                          value={time}
                                          disabled={
                                            toMinutes(time) <=
                                            toMinutes(startTime)
                                          }
                                        >
                                          {time}
                                        </option>
                                      );
                                    })}
                                  </select>
                                  <button
                                    onClick={() => addExceptionSlot(date)}
                                    className={`h-7 px-3 rounded-md text-[12px] font-medium transition-colors ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
                                  >
                                    + Toevoegen
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className={`px-6 py-4 border-t flex justify-end gap-3 ${isDark ? "border-white/[0.06]" : "border-gray-100"}`}
        >
          <button
            onClick={() => onOpenChange(false)}
            className={`h-9 px-4 rounded-lg text-[12px] font-medium border transition-colors ${isDark ? "border-white/[0.08] text-gray-400 hover:bg-white/[0.04]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}
          >
            Annuleren
          </button>
          <button
            onClick={() => onSave(localSettings)}
            className={`h-9 px-5 rounded-lg text-[12px] font-medium transition-colors ${isDark ? "bg-white text-black hover:bg-gray-200" : "bg-gray-900 text-white hover:bg-gray-800"}`}
          >
            Opslaan
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReservationSettingsModal;
